import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import {
  createCoupon,
  deleteCoupon,
  getMerchantMe,
  listCoupons,
  listProducts,
  updateCoupon,
  type MerchantCoupon,
  type MerchantCouponInput,
  type MerchantProduct,
} from '../lib/api';

/**
 * Merchant coupon CMS. A merchant creates discount codes scoped to its OWN
 * products. Coupons apply to CASH order lines only and never touch PV — the
 * server enforces both that and product ownership. Mirrors the merchant
 * products page's gate + table + dialog patterns.
 */
export function CouponsPage() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  const approved = me?.merchant.status === 'approved';
  const { data: coupons = [], isLoading } = useQuery({
    queryKey: ['merchant-coupons'],
    queryFn: listCoupons,
    enabled: approved,
  });
  const { data: products = [] } = useQuery({
    queryKey: ['merchant-products'],
    queryFn: listProducts,
    enabled: approved,
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MerchantCoupon | null>(null);

  const del = useMutation({
    meta: { successMessage: 'Coupon deleted.' },
    mutationFn: (id: string) => deleteCoupon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant-coupons'] }),
  });

  if (me && !approved) {
    return (
      <div className="max-w-4xl space-y-4">
        <h1 className="text-2xl font-semibold">Coupons</h1>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Your merchant account is{' '}
            <span className="font-medium capitalize">{me.merchant.status}</span>. You can manage
            coupons once an admin approves your account.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Coupons</h1>
        <Button onClick={() => setCreating(true)} disabled={products.length === 0}>
          New coupon
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Discount codes for your own products. Coupons apply to cash order lines only and never
        affect PV.
      </p>

      {products.length === 0 && (
        <Card>
          <CardContent className="py-6 text-center text-muted-foreground">
            Add a product first — coupons must target at least one of your products.
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : coupons.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No coupons yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="p-3">Code</th>
                  <th className="p-3">Discount</th>
                  <th className="p-3">Used</th>
                  <th className="p-3">Validity</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="p-3 font-mono font-medium">{c.code}</td>
                    <td className="p-3 tabular-nums">
                      {c.discountType === 'percent'
                        ? `${Number(c.discountValue)}%`
                        : `₱${Number(c.discountValue).toFixed(2)}/unit`}
                    </td>
                    <td className="p-3 tabular-nums">
                      {c.usedCount}
                      {c.maxUses != null ? ` / ${c.maxUses}` : c.oneTime ? ' / 1' : ''}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{validityLabel(c)}</td>
                    <td className="p-3">
                      <Badge variant={c.active ? 'success' : 'secondary'}>
                        {c.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => setEditing(c)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="ml-3 text-xs text-destructive hover:underline"
                        disabled={del.isPending}
                        onClick={() => {
                          if (confirm(`Delete coupon ${c.code}?`)) del.mutate(c.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {creating && <CouponDialog products={products} onClose={() => setCreating(false)} />}
      {editing && (
        <CouponDialog
          key={editing.id}
          coupon={editing}
          products={products}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function validityLabel(c: MerchantCoupon): string {
  const fmt = (s: string | null) => (s ? new Date(s).toLocaleDateString() : null);
  const from = fmt(c.validFrom);
  const until = fmt(c.validUntil);
  if (from && until) return `${from} – ${until}`;
  if (until) return `until ${until}`;
  if (from) return `from ${from}`;
  return 'Always';
}

const toDateInput = (s: string | null | undefined) => (s ? s.slice(0, 10) : '');
const fromDateInput = (s: string) => (s.trim() === '' ? null : new Date(s).toISOString());

function CouponDialog({
  coupon,
  products,
  onClose,
}: {
  coupon?: MerchantCoupon;
  products: MerchantProduct[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!coupon;
  const [code, setCode] = useState(coupon?.code ?? '');
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>(
    coupon?.discountType ?? 'percent',
  );
  const [discountValue, setDiscountValue] = useState(
    coupon ? String(Number(coupon.discountValue)) : '',
  );
  const [oneTime, setOneTime] = useState(coupon?.oneTime ?? false);
  const [maxUses, setMaxUses] = useState(coupon?.maxUses != null ? String(coupon.maxUses) : '');
  const [perUserLimit, setPerUserLimit] = useState(
    coupon?.perUserLimit != null ? String(coupon.perUserLimit) : '',
  );
  const [validFrom, setValidFrom] = useState(toDateInput(coupon?.validFrom));
  const [validUntil, setValidUntil] = useState(toDateInput(coupon?.validUntil));
  const [active, setActive] = useState(coupon?.active ?? true);
  const [productIds, setProductIds] = useState<string[]>(coupon?.productIds ?? []);

  const save = useMutation({
    meta: { successMessage: 'Coupon saved.' },
    mutationFn: () => {
      const body: MerchantCouponInput = {
        discountType,
        discountValue: discountValue.trim(),
        oneTime,
        maxUses: maxUses.trim() === '' ? null : Number(maxUses),
        perUserLimit: perUserLimit.trim() === '' ? null : Number(perUserLimit),
        validFrom: fromDateInput(validFrom),
        validUntil: fromDateInput(validUntil),
        active,
        productIds,
      };
      return isEdit ? updateCoupon(coupon!.id, body) : createCoupon({ ...body, code: code.trim() });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-coupons'] });
      onClose();
    },
  });

  const valid =
    (isEdit || code.trim().length > 0) &&
    discountValue.trim() !== '' &&
    Number(discountValue) > 0 &&
    productIds.length > 0;

  function toggleProduct(id: string, on: boolean) {
    setProductIds((prev) => (on ? [...new Set([...prev, id])] : prev.filter((p) => p !== id)));
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${coupon!.code}` : 'New coupon'}</DialogTitle>
          <DialogDescription>
            Applies to cash order lines on the selected products. PV is never discounted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!isEdit && (
            <Field label="Code">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SAVE10" />
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Discount type">
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as 'percent' | 'amount')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="percent">Percent (%)</option>
                <option value="amount">Amount (₱ / unit)</option>
              </select>
            </Field>
            <Field label={discountType === 'percent' ? 'Percent off' : 'Amount off (per unit)'}>
              <Input value={discountValue} inputMode="decimal" onChange={(e) => setDiscountValue(e.target.value)} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Max total uses (blank = ∞)">
              <Input value={maxUses} inputMode="numeric" onChange={(e) => setMaxUses(e.target.value)} disabled={oneTime} />
            </Field>
            <Field label="Per-member limit (blank = ∞)">
              <Input value={perUserLimit} inputMode="numeric" onChange={(e) => setPerUserLimit(e.target.value)} />
            </Field>
            <Field label="Valid from">
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            </Field>
            <Field label="Valid until">
              <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </Field>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={oneTime} onChange={(e) => setOneTime(e.target.checked)} />
              Single global use
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active
            </label>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Eligible products</label>
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
              {products.length === 0 ? (
                <p className="text-xs text-muted-foreground">You have no products to apply this to.</p>
              ) : (
                products.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={productIds.includes(p.id)}
                      onChange={(e) => toggleProduct(p.id, e.target.checked)}
                    />
                    <span className="truncate">{p.name}</span>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                      ₱{Number(p.price).toFixed(2)}
                    </span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">Pick at least one of your products.</p>
          </div>

          {save.error ? (
            <p className="text-sm text-destructive">Could not save the coupon. Check the fields (the code may be taken).</p>
          ) : null}

          <div className="flex items-center gap-3">
            <Button disabled={!valid || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create coupon'}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
